/**
 * DetailViewCommon Component
 *
 * This component is used to display the common elements of an event detail screen across different platforms.
 * It handles data display such as the event's name, date, time, and location.
 *
 * Props:
 *  - route: Navigation route that includes event details.
 *  - FontAwesomeIcon: The FontAwesome icon component used for displaying icons.
 *  - iconSize: The size of the FontAwesome icon.
 *
 * @file DetailViewCommon.js
 * @module DetailViewCommon
 */
import React, { useEffect, useState } from "react";
import { View, StyleSheet, Dimensions, Platform, Linking, TouchableOpacity, Pressable, Text, ScrollView } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { COLORS, STRINGS } from "../../../constants";
import Heading from "../../../components/Heading/Heading";
import SummaryCard from "../../../components/SummaryCard/SummaryCard";
import {
  faGlobe,
  faCalendarAlt,
  faLocationDot,
  faPhone,
  faImage,
  faShareAlt,
  faGauge,
  faBuilding,
  faParking,
  faBus,
} from "@fortawesome/free-solid-svg-icons";
import DetailDescription from "../../../components/DetailDescription/DetailDescription";
import DetailButtonArray from "../../../components/DetailButtonArray/DetailButtonArray";
import { formatAddress, toTitleCase, parkingFormatAddress } from "../../../utils/formatting";
import EventStore from "../../../storage/eventStore";
import ParkingStore from "../../../storage/parkingStore";
import { getNearbyLocations } from "../../../utils/geolocation";
import Loading from "../../../components/Loading/Loading";
import PageNotFound from "../../../components/NoResults/PageNotFound";
import { share, supportsShare, openDirections, addToCalendar } from "../../../utils/helper";
import { logAnalyticsEvent } from "../../../utils/firebase";
import useMetaDescriptionOnFocus from "../../../components/Meta/MetaDescription";
import { fetchNearbyBusStops } from "../../../utils/api";

const DetailViewCommon = ({ route, FontAwesomeIcon, iconSize }) => {
  useEffect(() => {
    logAnalyticsEvent("screen_view", { name: "VBGo-EventsDetailView" });
  }, []);

  const [busStops, setBusStops] = useState([]);
  const [busStopsLoading, setBusStopsLoading] = useState(false);

  const featuredEvents = EventStore((state) => state.featuredEvents);
  const fetchEventByKey = EventStore((state) => state.fetchEventByKey);
  const selectedEvent = EventStore((state) => state.selectedEvent);
  const eventKey = route.params?.eventKey;
  const featuredEvent = featuredEvents.find((item) => item.occurrence_id === eventKey);
  const event = featuredEvent || (selectedEvent?.occurrence_id === eventKey ? selectedEvent : null);
  const initialLoading = EventStore((state) => state.initialLoading);
  const navigation = useNavigation();
  const allParkingData = ParkingStore((state) => state.parking);
  useMetaDescriptionOnFocus(event?.searchDescription);

  /**
   * convertMetersToFeet
   *
   * Converts a distance in meters to feet and rounds to the nearest whole number.
   * Formula: feet = meters * 3.28084
   *
   * Props:
   * - meters (number): Distance in meters to be converted.
   *
   * Returns:
   * - number: Distance in feet, rounded to nearest whole number.
   */
  const convertMetersToFeet = (meters) => {
    // Convert to number if string
    if (typeof meters === 'string') {
      meters = parseFloat(meters);
    }
    if (typeof meters !== 'number' || isNaN(meters)) {
      return 0;
    }
    const METERS_TO_FEET = 3.28084;
    return Math.round(meters * METERS_TO_FEET);
  };

  /**
   * Generates an array of header button configurations based on the properties of the current event.
   * Each button is conditionally included depending on the presence of relevant event data.
   *
   * @returns {Array<Object>} Array of button objects for the event header.
   * Each object may contain:
   *   - icon: Icon definition for the button.
   *   - label: Text label for the button.
   *   - color: Color value for the button.
   */
  const initHeaderButtons = () => {
    return [
      event?.images && event?.images?.length > 0 ? { icon: faImage, label: "Photos", color: COLORS.border } : null,
      event?.name && event?.location?.name && supportsShare()
        ? { icon: faShareAlt, label: "Share", color: COLORS.border }
        : null,
      event?.location?.url ? { icon: faGlobe, label: "Website", color: COLORS.border } : null,
      event?.location?.phone && !(Platform.isPad || Platform.OS === "web")
        ? { icon: faPhone, label: "Call", color: COLORS.border }
        : null,
    ].filter(Boolean);
  };

  /**
   * handleRoutePress
   *
   * Opens the HRT route information page for the given route number.
   * Constructs the URL as `https://gohrt.com/route/{routeNumber}/` and opens it using Linking.
   * Includes error handling to catch and log any errors without crashing the app.
   *
   * Props:
   * - routeNumber (string): The route number or identifier (e.g., "2A", "10X")
   *
   * Returns:
   * - Promise: Resolves when the URL is opened or error is caught
   */
  const handleRoutePress = async (routeNumber) => {
    try {
      const url = `https://gohrt.com/route/${routeNumber}/`;
      await Linking.openURL(url);
    } catch (error) {
      console.error(`Failed to open route URL for route ${routeNumber}:`, error);
    }
  };

  /**
   * renderRouteBadges
   *
   * Renders route badges for a comma-separated route string. Each route is displayed
   * as a green circular badge with white text in a horizontal ScrollView.
   *
   * Validates: Requirements 1.2 Route Badges
   *
   * Props:
   * - routesString (string): A comma-separated string of route numbers (e.g., "1,2A,15X")
   *
   * Returns:
   * - JSX with "Routes:" label and horizontally-scrollable row of badges, or null if empty/null/whitespace
   *
   * Behavior:
   * - Returns null if routesString is empty, null, or only whitespace
   * - Splits by commas, trims each route, and filters empty strings
   * - Renders each route as a Pressable badge in a horizontal ScrollView
   * - Each badge calls handleRoutePress() with the route number when tapped
   * - Uses correct styles from stylesheet: routeBadge, routeText, routesContainer, routesLabel, routesScroll
   * - Includes proper styling: green background, white text, centered, with appropriate padding and margins
   */
  const renderRouteBadges = (routesString) => {
    // Return null if string is empty, null, or only whitespace
    if (!routesString || routesString.trim() === "") {
      return null;
    }

    // Split by commas, trim each route number, and filter out empty strings
    const routes = routesString
      .split(",")
      .map((route) => route.trim())
      .filter(Boolean);

    // If no valid routes after filtering, return null
    if (routes.length === 0) {
      return null;
    }

    // Render container with label and horizontal scrollable row of badges
    return (
      <View style={styles.routesContainer}>
        <Text style={styles.routesLabel}>{STRINGS.routes}:</Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.routesScroll}
        >
          {routes.map((route, idx) => (
            <Pressable
              key={idx}
              onPress={() => handleRoutePress(route)}
              style={styles.routeBadge}
            >
              <Text style={styles.routeText}>{route}</Text>
            </Pressable>
          ))}
        </ScrollView>
      </View>
    );
  };

  /**
   * handlePress
   *
   * This function is a simple handler for button press events. It logs the label of the pressed
   * button to the console for debugging or action tracking purposes.
   *
   * Props:
   * - button (object): The button object containing the `label` property to be logged.
   *
   * Returns:
   * - None (This function performs a console log as an action).
   */
  const handlePress = (button) => {
    const { label } = button;
    switch (label) {
      case "Share": {
        const url = `\n\n${process.env.REACT_APP_APPURL}/events/details?eventKey=${eventKey}`;
        const message = `Check out this event: ${event.name} at ${event.location.name}. Address: ${formattedAddress}${url}`;
        share(message, event.name);
        break;
      }
      case "Call":
        if (event.location?.phone) Linking.openURL(`tel:${event.location.phone}`);
        break;
      case "Website":
        if (event.location?.url) {
          let url = event.location.url;
          if (!url.startsWith("http://") && !url.startsWith("https://")) {
            url = "https://" + url;
          }
          Linking.openURL(url);
        }
        break;
      case "Link":
        if (event.occurrences[0].url) Linking.openURL(event.occurrences[0].url);
        break;
      case "Photos":
        navigation.navigate("ImageGalleryModal", {
          data: event,
          accessibilityLabel: `${event.name} ${STRINGS.picture}`,
        });
        break;
      default:
        console.log(`Button pressed: ${label}`);
    }
  };

  const parkingTypeIcons = {
    meter: faGauge,
    structure: faBuilding,
    default: faParking,
  };
  useEffect(() => {
    if (!event || Object.keys(event).length === 0) {
      fetchEventByKey(eventKey);
    }
  }, [event, eventKey]);

  useEffect(() => {
    if (event) {
      navigation.setOptions({
        title: event?.name,
        headerRight: () => (
          <DetailButtonArray
            FontAwesomeIcon={FontAwesomeIcon}
            buttons={initHeaderButtons()}
            onPress={handlePress}
            iconSize={Platform.OS === "web" ? "2x" : iconSize}
          />
        ),
        headerRightContainerStyle: styles.headerRight,
        headerTitleStyle: styles.headerTitle,
      });
    }
  }, [event]);

  useEffect(() => {
    if (event?.location?.latitude == null || event?.location?.longitude == null) {
      setBusStops([]);
      setBusStopsLoading(false);
      return;
    }

    setBusStopsLoading(true);

    fetchNearbyBusStops(event.location.latitude, event.location.longitude)
      .then((stops) => {
        setBusStops(stops);
        setBusStopsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching bus stops:", error);
        setBusStops([]);
        setBusStopsLoading(false);
      });
  }, [event?.occurrence_id]);

  if (initialLoading) {
    return (
      <View style={styles.container}>
        <Loading />
      </View>
    );
  }

  if (!event || Object.keys(event).length === 0) {
    return (
      <View style={styles.container}>
        <PageNotFound />
      </View>
    );
  }

  const occurrences = event?.occurrences?.[0];

  let parkingData = [];

  if (event.location && event.location.latitude && event.location.longitude) {
    parkingData = getNearbyLocations(
      {
        latitude: event.location.latitude,
        longitude: event.location.longitude,
      },
      allParkingData,
      0.2,
      5
    );
  }

  const formatted = occurrences?.formatted || {};
  const formattedDate = `${formatted?.startDate || ""}`;
  const formattedDay = new Date(occurrences?.start).toLocaleDateString("en-US", {
    weekday: "long",
  });
  const formattedTime = `${formatted?.times}`;

  const formattedAddress = formatAddress(event?.location?.address, event?.location?.city, event?.location?.zip);

  /**
   * renderItemName
   *
   * This function is responsible for rendering the name of a parking item with proper title casing.
   * It takes a name string, converts it to title case, and displays it using the parkingName style.
   *
   * Props:
   * - name (string): The name of the parking item to be displayed.
   *
   * Returns:
   * - A Text component displaying the formatted name.
   */
  const renderItemName = (name) => (
    <TouchableOpacity onPress={() => navigateToParkingDetails(name)}>
      <Text style={styles.parkingName}>{toTitleCase(name)}</Text>
    </TouchableOpacity>
  );

  /**
   * renderItemDetails
   *
   * This function renders the details of a parking item, including the number of available spaces
   * and the formatted address. It concatenates the available spaces with a dot separator and the
   * formatted address of the parking item.
   *
   * Props:
   * - item (object): The parking item containing `freeSpaces` and `address` properties.
   *
   * Returns:
   * - A Text component displaying the parking details in a specific format.
   */
  const renderItemDetails = (item) => (
    <TouchableOpacity onPress={() => navigateToParkingDetails(item.name)}>
      <Text style={styles.parkingDetails}>
        {/* {item.freeSpaces} {STRINGS.spacesAvailable}
        <Text style={styles.dotStyle}> • </Text> */}
        {parkingFormatAddress(item.address)}
      </Text>
    </TouchableOpacity>
  );

  /**
   * renderBusStopDetails
   *
   * Displays the full bus stop info: distance + name + routes.
   * This receives the full bus stop object from DetailList.
   *
   * Requirements: 1.1 Display Bus Stops, 1.2 Route Badges
   *
   * Props:
   * - busStop (object): Bus stop object with all properties
   *
   * Returns:
   * - JSX View with distance, name, and route badges
   */
  const renderBusStopDetails = (busStop) => {
    const hasValidCoords =
      busStop.latitude != null &&
      busStop.longitude != null &&
      !isNaN(parseFloat(busStop.latitude)) &&
      !isNaN(parseFloat(busStop.longitude));

    const nameText = <Text style={styles.busStopName}>{busStop.name}</Text>;

    return (
      <View>
        {hasValidCoords ? (
          <Pressable
            onPress={() => openDirections(busStop.latitude, busStop.longitude)}
            accessibilityLabel={`Get directions to ${busStop.name}`}
            accessibilityRole="button"
          >
            {nameText}
          </Pressable>
        ) : (
          nameText
        )}
        {renderRouteBadges(busStop.routesservedbystop)}
      </View>
    );
  };

  const navigateToParkingDetails = (name) => {
    const index = allParkingData.findIndex((item) => item.name === name);
    if (index !== -1) {
      navigation.navigate("ParkingModal", { index });
    }
  };

  const headerComponent = () => (
    <>
      <Heading text={event.name} fontSize={26} />
      {event.categoriesV2 && event.categoriesV2.length > 0 && (
        <View style={styles.categoriesContainer}>
          <Text style={styles.categoriesHeader}>
            {event.categoriesV2.length === 1 ? STRINGS.category : STRINGS.categories}:
          </Text>
          <Text style={styles.categoriesText}>{event.categoriesV2.join(", ")}</Text>
        </View>
      )}
      <View style={styles.cardsContainer}>
        {occurrences ? (
          <Pressable
            testID="add-to-calendar"
            onPress={() =>
              addToCalendar(
                event.name,
                new Date(occurrences?.start + "Z").toLocaleString("sv-SE", { timeZone: "America/New_York" }),
                new Date(occurrences?.end + "Z").toLocaleString("sv-SE", { timeZone: "America/New_York" }),
                event.location?.address,
                event.description,
                event.occurrences[0].url
              )
            }
          >
            <SummaryCard
              heading={formattedDate}
              description={`${formattedDay}, ${formattedTime}`}
              icon={faCalendarAlt}
              size={iconSize}
              iconColor={COLORS.primary}
              FontAwesomeIcon={FontAwesomeIcon}
            />
          </Pressable>
        ) : (
          <SummaryCard
            heading={STRINGS.timingUnavailable}
            description={STRINGS.timingUnavailable}
            icon={faCalendarAlt}
            size={iconSize}
            iconColor={COLORS.warning}
            FontAwesomeIcon={FontAwesomeIcon}
          />
        )}
        <Pressable
          style={styles.locationCard}
          onPress={() => {
            if (event.location?.latitude && event.location?.longitude) {
              openDirections(event.location.latitude, event.location.longitude);
            }
          }}
        >
          <SummaryCard
            heading={event.location?.name || STRINGS.locationUnavailable}
            description={formattedAddress}
            icon={faLocationDot}
            size={iconSize}
            iconColor={COLORS.primary}
            FontAwesomeIcon={FontAwesomeIcon}
          />
        </Pressable>
      </View>
      <DetailDescription
        title={STRINGS.aboutEvent}
        description={event.description}
        FontAwesomeIcon={FontAwesomeIcon}
        iconSize={iconSize}
        disclaimer={!(event.location && (event.location.name || event.location.address))}
        eventUrl={event?.occurrences?.[0]?.url}
      />
    </>
  );

  return (
    <View style={styles.container}>
      {initialLoading && <Loading />}
      {!initialLoading && (
        <ScrollView contentContainerStyle={styles.scrollContainer}>
          {headerComponent()}
          {parkingData.length > 0 && <Heading fontSize={18} text={STRINGS.parkingOptions} color={COLORS.textDark} />}
          {parkingData.length > 0 && (
            <View style={styles.listContainer}>
              {parkingData.map((item, index) => (
                <View key={index} style={styles.itemCard}>
                  <View style={styles.iconContainer}>
                    <FontAwesomeIcon
                      icon={parkingTypeIcons[item.type] || parkingTypeIcons.default}
                      size={Platform.OS === "web" ? "xl" : iconSize}
                      color={COLORS.primary}
                    />
                  </View>
                  <View style={styles.descriptionContainer}>
                    {renderItemName(item.name)}
                    {renderItemDetails(item)}
                  </View>
                </View>
              ))}
            </View>
          )}
          {busStopsLoading && (
            <View style={styles.listContainer}>
              <Heading fontSize={18} text={STRINGS.busStops} color={COLORS.textDark} />
              <View style={styles.loadingContainer}>
                <Loading />
              </View>
            </View>
          )}
          {!busStopsLoading && busStops.length > 0 && (
            <View style={styles.listContainer}>
              <Heading fontSize={18} text={STRINGS.busStops} color={COLORS.textDark} />
              {busStops.map((stop, index) => {
                const hasValidCoords =
                  stop.latitude != null &&
                  stop.longitude != null &&
                  !isNaN(parseFloat(stop.latitude)) &&
                  !isNaN(parseFloat(stop.longitude));

                const busIcon = (
                  <FontAwesomeIcon
                    icon={faBus}
                    size={Platform.OS === "web" ? "xl" : iconSize}
                    color={COLORS.primary}
                  />
                );

                return (
                  <View key={index} style={styles.itemCard}>
                    <View style={styles.iconContainer}>
                      {hasValidCoords ? (
                        <Pressable
                          onPress={() => openDirections(stop.latitude, stop.longitude)}
                          accessibilityLabel={`Get directions to ${stop.name}`}
                          accessibilityRole="button"
                        >
                          {busIcon}
                        </Pressable>
                      ) : (
                        busIcon
                      )}
                    </View>
                    <View style={styles.descriptionContainer}>
                      {renderBusStopDetails(stop)}
                    </View>
                  </View>
                );
              })}
            </View>
          )}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: "flex-start",
    paddingBottom: Dimensions.get("window").height * 0.1,
  },
  cardsContainer: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  locationCard: {
    marginTop: -16,
  },
  parkingName: {
    fontSize: 16,
    fontWeight: Platform.OS == "web" ? 500 : "bold",
    color: COLORS.textDark,
    lineHeight: 24,
    marginBottom: 4,
  },
  parkingDetails: {
    fontSize: 16,
    color: COLORS.border,
    lineHeight: 28,
  },
  dotStyle: {
    fontSize: 18,
    color: COLORS.border,
  },
  chipScrollContainer: {
    paddingVertical: 12,
    gap: 8,
    flexDirection: "row",
  },
  categoryChip: {
    marginRight: 8,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 8,
    justifyContent: "flex-end",
  },
  headerTitle: {
    display: "none",
  },
  categoriesContainer: {
    marginTop: 12,
    gap: 8,
  },
  categoriesHeader: {
    fontSize: 18,
    fontWeight: "bold",
    color: COLORS.textDark,
  },
  categoriesText: {
    fontSize: 16,
    color: COLORS.border,
  },
  busStopRow: {
    flexDirection: "row",
    gap: 12,
    alignItems: "center",
  },
  busStopText: {
    fontSize: 14,
    color: COLORS.border,
  },
  busStopName: {
    fontSize: 16,
    fontWeight: Platform.OS === "web" ? 500 : "bold",
    color: COLORS.textDark,
  },
  routesContainer: {
    flexDirection: "row",
    marginTop: 12,
    alignItems: "center",
  },
  routesLabel: {
    fontSize: 14,
    color: COLORS.border,
  },
  routesScroll: {
    flex: 1,
    marginLeft: 12,
  },
  routeBadge: {
    backgroundColor: COLORS.primary,
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
  },
  routeText: {
    color: "white",
    fontSize: 12,
    fontWeight: Platform.OS === "web" ? 500 : "bold",
    textAlign: "center",
  },
  itemCard: {
    flexDirection: "row",
    marginBottom: 10,
    paddingTop: 16,
  },
  iconContainer: {
    marginRight: Platform.OS === "web" ? 20 : 16,
  },
  descriptionContainer: {
    flex: 1,
  },
  listContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  loadingContainer: {
    paddingVertical: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  busStopsSection: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
  },
});

export default DetailViewCommon;
